'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Poppins } from 'next/font/google';
import { BRAND_BLUE, BRAND_GREEN, BRAND_YELLOW } from '@voley/shared-types/constants';

const poppins = Poppins({ subsets: ['latin'], weight: ['700'] });

// Brand colors
const primaryColor = BRAND_BLUE;
const accentGreen = BRAND_GREEN;
const accentYellow = BRAND_YELLOW;

export default function TalkToSalesPage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/public/external-requests/talk-to-sales', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit request');
      }

      setIsSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 pb-16">
        {/* Header */}
        <div className="text-center mb-8">
          <h1
            className={`text-4xl sm:text-5xl md:text-6xl ${poppins.className} tracking-tight leading-tight`}
          >
            <span className="block font-medium text-gray-900">
              Let&apos;s talk.
            </span>
          </h1>
          <p className="mt-3 text-base sm:text-lg text-gray-600">
            Leave your info and we&apos;ll reach out shortly.
          </p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: primaryColor }}
            />
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: accentGreen }}
            />
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: accentYellow }}
            />
          </div>
        </div>

        {/* Form Card */}
        <div
          className="rounded-2xl p-1.5"
          style={{ background: `linear-gradient(135deg, ${primaryColor}30 0%, ${accentGreen}30 50%, ${accentYellow}30 100%)` }}
        >
          <div className="bg-white rounded-xl p-6 sm:p-8">
            {!isSubmitted ? (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="h-10 w-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${primaryColor}15` }}
                  >
                    <svg className="h-5 w-5" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Request a callback</h2>
                    <p className="text-sm text-gray-500">We typically respond within a few hours</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {error}
                    </div>
                  )}

                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      required
                      autoComplete="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="focus:ring-2 focus:ring-brand focus:border-transparent block w-full text-sm rounded-lg p-3 border border-gray-300 transition-shadow"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      required
                      autoComplete="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="focus:ring-2 focus:ring-brand focus:border-transparent block w-full text-sm rounded-lg p-3 border border-gray-300 transition-shadow"
                      placeholder="555-123-4567"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="focus:ring-2 focus:ring-brand focus:border-transparent block w-full text-sm rounded-lg p-3 border border-gray-300 transition-shadow"
                      placeholder="you@company.com"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex justify-center items-center px-4 py-3 border-0 text-base font-medium rounded-full text-white hover:shadow-lg transition-all hover:scale-[1.01] disabled:opacity-70 disabled:cursor-not-allowed mt-2"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Request a call'}
                  </button>
                </form>

                <p className="mt-5 text-center text-sm text-gray-500">
                  Prefer email? <Link href="/contact" className="text-brand hover:underline font-medium">Contact us here</Link>
                </p>
              </>
            ) : (
              <div className="text-center py-6">
                <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full mb-5" style={{ backgroundColor: `${accentGreen}20` }}>
                  <svg className="h-7 w-7 text-[#5aaa7a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">We&apos;ll be in touch!</h3>
                <p className="text-gray-600 text-sm mb-6">
                  Our team will reach out to you shortly at the number you provided.
                </p>
                <Link
                  href="/"
                  className="inline-flex items-center justify-center px-6 py-2.5 border-0 text-sm font-medium rounded-full text-white hover:shadow-lg transition-all hover:scale-[1.01]"
                  style={{ backgroundColor: primaryColor }}
                >
                  Return to home
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Trust note */}
        {!isSubmitted && (
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>Your information is secure and private</span>
          </div>
        )}
      </div>
    </div>
  );
}
